import { WEIGHT_UNIT_OUNCES, WEIGHT_UNIT_GRAMS } from '../types/weight';
import { convertWeight, isWeightUnit, isImperialWeight } from '../utils/weight-utils';

import type { Weight, WeightUnit } from '../types/weight';

type HandlingUnitWeightSource = {
  quantity?: number | null;
  commodities?: ReadonlyArray<{ weight?: number | null; weightUnit?: string | null }> | null;
};

/**
 * Derives the total weight of a set of handling units by summing each unit's commodity weights,
 * multiplied by the handling unit's quantity, and converting everything to `targetUnit`.
 *
 * Each commodity's `weight` represents the total for one handling unit's worth of that commodity,
 * so `unit.quantity` is the only multiplier applied. `commodity.quantity` is not a multiplier.
 *
 * Commodity weights are persisted in the seller's minor unit (oz or g) by `WeightInput`, so when
 * `commodity.weightUnit` is absent or `'Unknown'`, we fall back to the minor unit of `targetUnit`
 * rather than `targetUnit` itself — using a major unit would inflate the value 16× or 1000×.
 *
 * Returns `{ value: 0, unit: targetUnit }` when `handlingUnits` is null/undefined/empty.
 */
export const calculateHandlingUnitsTotalWeight = (
  handlingUnits: ReadonlyArray<HandlingUnitWeightSource> | null | undefined,
  targetUnit: WeightUnit,
): Weight => {
  if (!handlingUnits?.length) return { value: 0, unit: targetUnit };

  const minorUnit = isImperialWeight(targetUnit) ? WEIGHT_UNIT_OUNCES : WEIGHT_UNIT_GRAMS;

  const total = handlingUnits.reduce((unitSum, unit) => {
    const unitQuantity = unit.quantity ?? 1;
    const commodityTotal = (unit.commodities ?? []).reduce((commoditySum, commodity) => {
      const fromUnit = isWeightUnit(commodity.weightUnit) ? commodity.weightUnit : minorUnit;
      return commoditySum + convertWeight(commodity.weight ?? 0, fromUnit, targetUnit);
    }, 0);
    return unitSum + unitQuantity * commodityTotal;
  }, 0);

  return { value: total, unit: targetUnit };
};
